import React, { useState, useEffect } from 'react';
import {
  Grid,
  TextField,
  MenuItem,
  Button,
  Box,
  IconButton,
  Autocomplete,
  Chip
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import axios from 'axios';

interface FilterValues {
  invoiceType: string;
  invoicePages: string[];
  branchId: string;
  dateFrom: string;
  dateTo: string;
  productName: string;
  barcode: string;
}

interface ProductMovementFilterProps {
  filterValues: FilterValues;
  onFilterChange: (newValues: FilterValues) => void;
}

interface Branch {
  _id: string;
  name: string;
}

const ProductMovementFilter: React.FC<ProductMovementFilterProps> = ({
  filterValues,
  onFilterChange
}) => {
  const [localFilter, setLocalFilter] = useState<FilterValues>(filterValues);
  const [invoicePages, setInvoicePages] = useState<string[]>([]);
  const [invoiceTypes, setInvoiceTypes] = useState<string[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);

  // جلب صفحات الفواتير وأنواعها والفروع المتاحة
  useEffect(() => {
    const fetchData = async () => {
      try {
        // جلب صفحات الفواتير
        const pagesResponse = await axios.get('/api/reports/product-movement/invoice-pages');
        if (pagesResponse.data && pagesResponse.data.success && Array.isArray(pagesResponse.data.data)) {
          setInvoicePages(pagesResponse.data.data);
        }

        // جلب أنواع الفواتير
        const typesResponse = await axios.get('/api/reports/product-movement/invoice-types');
        if (typesResponse.data && typesResponse.data.success && Array.isArray(typesResponse.data.data)) {
          setInvoiceTypes(typesResponse.data.data);
        }

        // جلب الفروع
        const branchesResponse = await axios.get('/api/reports/product-movement/branches');
        if (branchesResponse.data && branchesResponse.data.success && Array.isArray(branchesResponse.data.data)) {
          setBranches(branchesResponse.data.data);
        }
      } catch (error) {
        console.error('خطأ في جلب البيانات:', error);
        setInvoicePages([]);
        setInvoiceTypes([]);
        setBranches([]);
      }
    };
    fetchData();
  }, []);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setLocalFilter(prev => ({ ...prev, [name]: value }));
  };

  const handleInvoicePagesChange = (event: any, newValue: string[]) => {
    setLocalFilter(prev => ({ ...prev, invoicePages: newValue }));
  };

  const applyFilter = () => {
    onFilterChange(localFilter);
  };

  const resetFilter = () => {
    const emptyFilter: FilterValues = {
      invoiceType: '',
      invoicePages: [],
      branchId: '',
      dateFrom: '',
      dateTo: '',
      productName: '',
      barcode: ''
    };
    setLocalFilter(emptyFilter);
    onFilterChange(emptyFilter);
  };

  return (
    <Box sx={{ my: 2 }}>
      <Grid container spacing={2}>
        {/* نوع الفاتورة */}
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            select
            fullWidth
            label="نوع الفاتورة"
            name="invoiceType"
            value={localFilter.invoiceType}
            onChange={handleInputChange}
            variant="outlined"
            size="small"
          >
            <MenuItem value="">
              <em>الكل</em>
            </MenuItem>
            {invoiceTypes.map(type => (
              <MenuItem key={type} value={type}>
                {type === 'sales_invoice' ? 'فاتورة مبيعات' :
                 type === 'sales_return' ? 'مرتجع مبيعات' :
                 type === 'purchase_invoice' ? 'فاتورة مشتريات' :
                 type === 'purchase_return' ? 'مرتجع مشتريات' :
                 type === 'quotation' ? 'عرض سعر' :
                 type === 'import_invoice_return' ? 'مرتجع فاتورة استيراد' :
                 type}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* صفحات الفواتير - اختيار متعدد */}
        <Grid item xs={12} sm={6} md={3}>
          <Autocomplete
            multiple
            fullWidth
            options={invoicePages}
            value={localFilter.invoicePages}
            onChange={handleInvoicePagesChange}
            renderInput={(params) => (
              <TextField
                {...params}
                label="صفحات الفواتير"
                variant="outlined"
                size="small"
                placeholder="اختر صفحة أو أكثر"
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  label={option}
                  size="small"
                  {...getTagProps({ index })}
                  sx={{
                    bgcolor: '#30c3b0',
                    color: 'white',
                    '& .MuiChip-deleteIcon': {
                      color: 'rgba(255, 255, 255, 0.7)',
                      '&:hover': {
                        color: 'white'
                      }
                    }
                  }}
                />
              ))
            }
            noOptionsText="لا توجد خيارات"
            sx={{
              '& .MuiAutocomplete-tag': {
                margin: '2px'
              }
            }}
          />
        </Grid>

        {/* الفرع */}
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            select
            fullWidth
            label="الفرع"
            name="branchId"
            value={localFilter.branchId}
            onChange={handleInputChange}
            variant="outlined"
            size="small"
          >
            <MenuItem value="">
              <em>الكل</em>
            </MenuItem>
            {branches.map(branch => (
              <MenuItem key={branch._id} value={branch._id}>
                {branch.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* التاريخ من */}
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            label="من تاريخ"
            name="dateFrom"
            type="date"
            value={localFilter.dateFrom}
            onChange={handleInputChange}
            variant="outlined"
            size="small"
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>

        {/* التاريخ إلى */}
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            label="إلى تاريخ"
            name="dateTo"
            type="date"
            value={localFilter.dateTo}
            onChange={handleInputChange}
            variant="outlined"
            size="small"
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>

        {/* اسم المنتج */}
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            label="اسم المنتج"
            name="productName"
            value={localFilter.productName}
            onChange={handleInputChange}
            variant="outlined"
            size="small"
            InputProps={{
              endAdornment: localFilter.productName ? (
                <IconButton
                  size="small"
                  onClick={() => {
                    setLocalFilter(prev => ({ ...prev, productName: '' }));
                  }}
                  edge="end"
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              ) : null
            }}
          />
        </Grid>

        {/* الباركود */}
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            label="الباركود"
            name="barcode"
            value={localFilter.barcode}
            onChange={handleInputChange}
            variant="outlined"
            size="small"
            InputProps={{
              endAdornment: localFilter.barcode ? (
                <IconButton
                  size="small"
                  onClick={() => {
                    setLocalFilter(prev => ({ ...prev, barcode: '' }));
                  }}
                  edge="end"
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              ) : null
            }}
          />
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: 1 }}>
        <Button
          variant="outlined"
          color="inherit"
          onClick={resetFilter}
        >
          إعادة تعيين
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={applyFilter}
          startIcon={<SearchIcon />}
          sx={{
            bgcolor: '#30c3b0',
            '&:hover': { bgcolor: '#25a899' },
          }}
        >
          بحث
        </Button>
      </Box>
    </Box>
  );
};

export default ProductMovementFilter;